import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { formatPrice } from '../utils/currency';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_API_URL}${url}`;
};

const ProductCard = ({ product, onClick, currency }) => (
  <div className="card product-card" onClick={onClick} style={{ minWidth: 220, maxWidth: 260, flex: '0 0 auto' }}>
    {(product.image_url || product.images?.[0]?.image_url)
      ? <img className="product-img" src={getImageUrl(product.image_url || product.images[0].image_url)} alt={product.name} />
      : <div className="product-img-placeholder">📦</div>
    }
    <div className="product-info">
      <h3>{product.name}</h3>
      <div className="product-price">{formatPrice(product.price, currency)}</div>
    </div>
  </div>
);

const HorizontalScroll = ({ children }) => {
  const ref = React.useRef(null);
  const scroll = (dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => scroll(-1)} style={{
        position: 'absolute', left: -20, top: '50%', transform: 'translateY(-50%)',
        zIndex: 2, background: 'white', border: '1px solid #e5e7eb', borderRadius: '50%',
        width: 40, height: 40, cursor: 'pointer', fontSize: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>‹</button>
      <div ref={ref} style={{
        display: 'flex', gap: '1.25rem', overflowX: 'auto', paddingBottom: '1rem',
        scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch',
      }}>
        {children}
      </div>
      <button onClick={() => scroll(1)} style={{
        position: 'absolute', right: -20, top: '50%', transform: 'translateY(-50%)',
        zIndex: 2, background: 'white', border: '1px solid #e5e7eb', borderRadius: '50%',
        width: 40, height: 40, cursor: 'pointer', fontSize: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>›</button>
    </div>
  );
};

const SectionsBanner = ({ sections, accentColor }) => {
  const scrollToSection = (id) => {
    const el = document.getElementById(`section-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };
  if (!sections || sections.length === 0) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', gap: '2rem',
      padding: '12px 30px', backgroundColor: '#fff',
      borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 99,
      flexWrap: 'wrap', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    }}>
      {sections.map(section => (
        <span key={section.id} onClick={() => scrollToSection(section.id)} style={{
          cursor: 'pointer', fontWeight: 600, fontSize: '14px',
          textTransform: 'uppercase', letterSpacing: '1px', color: '#1a1a1a',
          padding: '4px 0', borderBottom: '2px solid transparent', transition: 'border-color 0.2s, color 0.2s',
        }}
          onMouseEnter={e => { e.target.style.borderBottomColor = accentColor; e.target.style.color = accentColor; }}
          onMouseLeave={e => { e.target.style.borderBottomColor = 'transparent'; e.target.style.color = '#1a1a1a'; }}
        >
          {section.title}
        </span>
      ))}
    </div>
  );
};

const Home = () => {
  const [sections, setSections] = useState([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settings, setSettings] = useState({
    site_name: 'ecom',
    hero_title: 'Premium products, fast shipping.',
    hero_subtitle: 'Curated essentials for the modern lifestyle.',
    hero_image: '',
    hero_image_mobile: '',
    accent_color: '#c9a96e',
    button_text: 'Browse Collection →',
    hero_button_enabled: '1',
    hero_title_color: '#ffffff',
    section_title_color: '#1a1a1a',
    currency: 'MAD',
    home_layout: 'horizontal',
  });

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    api.get('/api/sections').then(({ data }) => setSections(data.data)).catch(console.error);

    api.get('/api/settings').then(({ data }) => {
      setSettings(s => ({ ...s, ...data.data }));
      setSettingsLoaded(true);
    }).catch(() => setSettingsLoaded(true));
  }, []);

  if (!settingsLoaded) return null;

  const heroImage = isMobile && settings.hero_image_mobile
    ? getImageUrl(settings.hero_image_mobile)
    : settings.hero_image ? getImageUrl(settings.hero_image) : null;

  const isGrid = settings.home_layout === 'grid';

  return (
    <>
      <section className="hero" style={{
        backgroundImage: heroImage ? `url(${heroImage})` : 'none',
        backgroundSize: 'cover', backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat', position: 'relative',
      }}>
        {heroImage && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 0 }} />}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ color: settings.hero_title_color }}>{settings.hero_title}</h1>
          <p>{settings.hero_subtitle}</p>
          {settings.hero_button_enabled === '1' && (
            <button className="btn btn-accent" onClick={() => navigate('/products')}>
              {settings.button_text}
            </button>
          )}
        </div>
      </section>

      <SectionsBanner sections={sections} accentColor={settings.accent_color} />

      {isGrid ? (
        <div className="container" style={{ padding: '4rem 1rem' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: '1.5rem',
          }}>
            {sections.map(section => {
              const firstProduct = section.products?.[0];
              const img = firstProduct?.image_url || firstProduct?.images?.[0]?.image_url;
              return (
                <div key={section.id} id={`section-${section.id}`}
                  onClick={() => navigate(`/products?section=${section.slug}`)}
                  style={{
                    cursor: 'pointer', borderRadius: 12, overflow: 'hidden',
                    border: '1px solid #e5e7eb', background: 'white',
                    transition: 'transform .2s, box-shadow .2s',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                >
                  <div style={{ width: '100%', aspectRatio: '1', overflow: 'hidden', background: '#f8f7f4' }}>
                    {img
                      ? <img src={getImageUrl(img)} alt={section.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>📦</div>
                    }
                  </div>
                  <div style={{ padding: '1rem', textAlign: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: settings.section_title_color, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                      {section.title}
                    </h3>
                    <p style={{ margin: '.25rem 0 0', color: '#888', fontSize: '.82rem' }}>
                      {section.products?.length || 0} produit{section.products?.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        sections.map(section => (
          <section key={section.id} id={`section-${section.id}`} style={{ padding: '4rem 0' }}>
            <div className="container">
              <h2 className="section-title" style={{ color: settings.section_title_color }}>
                {section.title}
              </h2>
              {section.products.length === 0 ? (
                <p className="section-subtitle">Aucun produit dans cette section.</p>
              ) : (
                <HorizontalScroll>
                  {section.products.map(p => (
                    <ProductCard key={p.id} product={p} currency={settings.currency}
                      onClick={() => navigate(`/products/${p.id}`)} />
                  ))}
                </HorizontalScroll>
              )}
              {section.products.length > 0 && (
                <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
                  <button className="btn btn-outline" onClick={() => navigate('/products')}>
                    View all products
                  </button>
                </div>
              )}
            </div>
          </section>
        ))
      )}

      <section style={{ background: '#1a1a1a', color: 'white', padding: '4rem 0' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem', textAlign: 'center' }}>
            {[
              { icon: '🚚', title: 'Fast Delivery', desc: 'Orders processed within 24 hours' },
              { icon: '🔒', title: 'Secure Checkout', desc: 'Your data is always protected' },
              { icon: '♻️', title: 'Easy Returns', desc: '30-day hassle-free returns' },
              { icon: '💬', title: 'Support', desc: 'We reply within 24 hours' },
            ].map(({ icon, title, desc }) => (
              <div key={title}>
                <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>{icon}</div>
                <h4 style={{ fontFamily: 'var(--font-head)', marginBottom: '.4rem' }}>{title}</h4>
                <p style={{ color: '#888', fontSize: '.9rem' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="footer" style={{ position: 'relative' }}>
        © {new Date().getFullYear()} <span>{settings.site_name}</span>. All rights reserved.
        <a href="/admin/login" style={{ position: 'absolute', bottom: 0, right: 0, padding: '.5rem 1rem', color: '#1a1a1a', fontSize: '.8rem', textDecoration: 'none' }}>Admin</a>
      </footer>
    </>
  );
};

export default Home;